import React, { useEffect, useRef, useState } from 'react'

import { useSelector } from 'react-redux'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'

import { UpdateZoneRequestDTO } from '@/core/api/dtos/request/zone/update'
import { Zone } from '@/core/api/entities/zone/zone'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import {
	useGroups,
	useAddSchedulesToGroup,
	useRemoveSchedulesFromGroup,
} from '@/core/hooks/useGroup'
import { useLocations } from '@/core/hooks/useLocation'
import { usePermission } from '@/core/hooks/usePermission'
import { useRecommendedSchedules, useSchedules } from '@/core/hooks/useSchedule'
import {
	useExportZones,
	useZone,
	useZones,
	useUpdateZone,
} from '@/core/hooks/useZone'
import { useTablePagination } from '@/core/hooks/utils/useTable'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import useZoneDeletion from '@/features/zones/hooks/useZoneDeletion'
import DeleteZoneModal from '@/features/zones/modals/DeleteZoneModal'
import { Button } from '@/shared/components/atoms/Button'
import {
	BottomSheet,
	BottomSheetContent,
	BottomSheetDescription,
	BottomSheetTitle,
} from '@/shared/components/molecules/BottomSheet/BottomSheet'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

import { StatusFilter } from './ZoneStatusFilter'
import { ZonesUI } from './ZonesUI'
import useZoneEdit from '../hooks/useZoneEdit'
import { useZoneFinishFlow } from '../hooks/useZoneFinishFlow'
import useZoneRefresh from '../hooks/useZoneRefresh'
import { useZoneSendCodeEmail } from '../hooks/useZoneSendCodeEmail'
import AddContentToZoneModal from '../modals/AddContentToZoneModal'
import AddGroupToZone from '../modals/AddGroupToZone'
import EditZoneModal from '../modals/EditZoneModal'
import RefreshZoneModal from '../modals/RefreshZoneModal'
import SendCodeEmailZoneModal from '../modals/SendCodeEmailZoneModal'
import ZoneCreatedSuccessModal from '../modals/ZoneCreatedSuccessModal'
import ZoneOnboardingModal from '../modals/ZoneOnboardingModal'
import ZoneOnboardingSuccessModal from '../modals/ZoneOnboardingSuccessModal'

const Zones: React.FC = () => {
	const [params, setParams] = useSearchParams()
	const pagination = useTablePagination({ defaultPage: 1, defaultPageSize: 10 })
	const locationPagination = useTablePagination({
		defaultPage: 1,
		defaultPageSize: 10,
	})
	const groupPagination = useTablePagination({
		defaultPage: 1,
		defaultPageSize: 10,
	})

	const { showNotification } = useNotificationContext()
	const { t } = useTranslation('zones')
	const { t: tCommon } = useTranslation('common')
	const { user } = useSelector((state: RootState) => state.auth)
	const selectedAccount = useSelector(
		(state: RootState) => state.selectedAccount.selectedAccount
	)
	const fetchAllDataEnabled = useSelector(
		(state: RootState) => state.fetchAllData.enabled
	)
	const currentAccount = useCurrentAccount()

	const [searchTerm, setSearchTerm] = useState(params.get('search') || '')
	const [searchQuery, setSearchQuery] = useState(params.get('search') || '')

	const [locationSearchTerm, setLocationSearchTerm] = useState(
		params.get('locationSearch') || ''
	)
	const [locationSearchQuery, setLocationSearchQuery] = useState(
		params.get('locationSearch') || ''
	)

	const [groupSearchTerm, setGroupSearchTerm] = useState(
		params.get('groupSearch') || ''
	)
	const [groupSearchQuery, setGroupSearchQuery] = useState(
		params.get('groupSearch') || ''
	)

	// Get routing navigation
	const navigate = useNavigate()
	const location = useLocation()

	const [showSuccessModal, setShowSuccessModal] = useState(false)
	const [newZoneId, setNewZoneId] = useState<number | null>(null)
	const [showFinishAssignModal, setShowFinishAssignModal] = useState(false)
	const [showFinishAddContentModal, setShowFinishAddContentModal] =
		useState(false)

	// Fetch the newly-created zone details when we need it for the finish flow.
	const { data: createdZone } = useZone({
		playerId: newZoneId,
		config: useAccountConfig({
			params: {
				include: 'groups',
			},
		}),
		options: {
			enabled:
				!!newZoneId && (showFinishAssignModal || showFinishAddContentModal),
		},
	})

	// Zone onboarding modal state
	const [showOnboardingModal, setShowOnboardingModal] = useState(false)
	const [selectedPendingZone, setSelectedPendingZone] = useState<Zone | null>(
		null
	)
	const [showConnectSheet, setShowConnectSheet] = useState(false)
	const [showOnboardingSuccessModal, setShowOnboardingSuccessModal] =
		useState(false)
	const onboardingModalShownRef = useRef(false)

	// Check for showGuide parameter (from guide navigation)
	const showGuideParam = params.get('showGuide') === 'true'

	// Handle status filter
	const [statusFilter, setStatusFilter] = useState<StatusFilter>(
		StatusFilter.ANY
	)

	// Account filter — only active in fetch-all-data mode
	const [accountFilter, setAccountFilter] = useState<number | null>(null)

	// Reset account filter when fetch-all-data is disabled
	useEffect(() => {
		if (!fetchAllDataEnabled) {
			setAccountFilter(null)
		}
	}, [fetchAllDataEnabled])

	// Use the deletion counter from Redux to trigger refetches
	const { deleteCounter } = useSelector((state: RootState) => state.zones)

	// Zone deletion hook
	const zoneDeletion = useZoneDeletion()

	// Zone refresh hook
	const zoneRefresh = useZoneRefresh({
		onSuccess: () => {
			refetch()
		},
	})

	// Zone send code email hook
	const zoneSendCodeEmail = useZoneSendCodeEmail()

	// Zone edit hook
	const zoneEdit = useZoneEdit({
		onSuccess: () => {
			refetch()
		},
	})

	// Zone update mutation
	const { mutate: updateZone, isPending: isUpdating } = useUpdateZone({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				showNotification({
					type: 'success',
					message: tCommon('entities.zone.edit.success.title'),
					description: tCommon('entities.zone.edit.success.description'),
				})
				refetch()
			},
			onError: (error: unknown) => {
				console.error('Error updating zone:', error)
				showNotification({
					type: 'error',
					message: tCommon('entities.zone.edit.error.title'),
					description: tCommon('common.try_again_later'),
				})
			},
		},
	})

	// Schedule mutations for onboarding
	const { mutate: addSchedulesToGroup } = useAddSchedulesToGroup({
		config: useAccountConfig({}),
	})

	const { mutate: removeSchedulesFromGroup } = useRemoveSchedulesFromGroup({
		config: useAccountConfig({}),
	})

	// Get search params from request
	const [searchParams, setSearchParams] = useSearchParams()

	// Handle tab change and update URL
	const validTabs = ['zones', 'group', 'locations']
	const activeTab =
		searchParams.get('tab') && validTabs.includes(searchParams.get('tab')!)
			? searchParams.get('tab')!
			: 'zones'

	const { features } = usePermission()

	// Redirect away from group tab if the feature is disabled
	useEffect(() => {
		if (activeTab === 'group' && !features.group.enabled) {
			const newSearchParams = new URLSearchParams(searchParams)
			newSearchParams.set('tab', 'zones')
			setSearchParams(newSearchParams, { replace: true })
		}
	}, [activeTab, features.group.enabled, searchParams, setSearchParams])

	// Reset zones pagination when status filter changes (prevents empty page when filtered results fit in fewer pages)
	useEffect(() => {
		if (activeTab !== 'zones') return
		pagination.onChange(1)
	}, [statusFilter, activeTab])

	// Reset all paginations when account filter changes (prevents empty page when new account has fewer pages)
	useEffect(() => {
		pagination.onChange(1)
		locationPagination.onChange(1)
		groupPagination.onChange(1)
	}, [accountFilter])

	// Handle tab change and update URL
	const handleTabChange = (tabKey: string) => {
		const newSearchParams = new URLSearchParams(searchParams)
		newSearchParams.set('tab', tabKey)
		setSearchParams(newSearchParams)
	}

	// Sync URL with default tab if no tab is present
	useEffect(() => {
		if (!searchParams.get('tab')) {
			const newSearchParams = new URLSearchParams(searchParams)
			newSearchParams.set('tab', 'zones')
			setSearchParams(newSearchParams, { replace: true })
		}
	}, [searchParams, setSearchParams])

	// Export zones
	const { mutate: exportZones } = useExportZones({
		config: useAccountConfig({}),
		options: {
			onSuccess: (result: import('@/core/hooks/useZone').ExportZonesResult) => {
				if (result.outcome === 'emailQueued') {
					showNotification({
						type: 'success',
						message: t('list.exportEmailQueued'),
						description: t('list.exportEmailQueuedDescription'),
					})
					return
				}

				try {
					const url = window.URL.createObjectURL(result.blob)
					const a = document.createElement('a')
					a.href = url
					a.download = `zones_${user?.account?.name || 'export'}_${new Date().toISOString().split('T')[0]}.xlsx`
					document.body.appendChild(a)
					a.click()
					window.URL.revokeObjectURL(url)
					document.body.removeChild(a)
				} catch (error) {
					console.error('Error downloading file:', error)
					showNotification({
						type: 'error',
						message: tCommon('common.download_error'),
						description: tCommon('common.try_again_later'),
					})
				}
			},
			onError: (error: unknown) => {
				console.error('Error exporting zones:', error)
				showNotification({
					type: 'error',
					message: tCommon('common.download_error'),
					description: tCommon('common.try_again_later'),
				})
			},
		},
	})

	// Create a debounced function that will update the searchQuery for API
	const debouncedSearch = useDebouncedCallback((value: string) => {
		// Update the searchQuery state for the API
		setSearchQuery(value)

		// Update URL params
		const newParams = new URLSearchParams(params)
		if (value === '') {
			newParams.delete('search')
		} else {
			newParams.set('search', value)
		}
		setParams(newParams.toString())

		// Reset pagination
		pagination.onChange(1)
	}, 1500)

	// Create a debounced function for locations
	const debouncedLocationSearch = useDebouncedCallback((value: string) => {
		setLocationSearchQuery(value)

		const newParams = new URLSearchParams(params)
		if (value === '') {
			newParams.delete('locationSearch')
		} else {
			newParams.set('locationSearch', value)
		}
		setParams(newParams.toString())

		locationPagination.onChange(1)
	}, 500)

	// Create a debounced function for groups
	const debouncedGroupSearch = useDebouncedCallback((value: string) => {
		setGroupSearchQuery(value)

		const newParams = new URLSearchParams(params)
		if (value === '') {
			newParams.delete('groupSearch')
		} else {
			newParams.set('groupSearch', value)
		}
		setParams(newParams.toString())

		groupPagination.onChange(1)
	}, 500)

	// Handle input changes - this only affects the UI
	const handleSearch = (value: string) => {
		setSearchTerm(value)
		debouncedSearch(value)
	}

	const handleLocationSearch = (value: string) => {
		setLocationSearchTerm(value)
		debouncedLocationSearch(value)
	}

	const handleGroupSearch = (value: string) => {
		setGroupSearchTerm(value)
		debouncedGroupSearch(value)
	}

	// Check if account is in trial by checking the subscription and that source is defined
	// Inbound customers will have their source specified. Outbound customers will not.
	const isTrialAccount =
		(currentAccount?.getTrialSubscriptions()?.length ?? 0) > 0 &&
		currentAccount?.source

	// Check if user is viewing another account (switch account mode)
	const isSwitchedAccount = selectedAccount !== null

	// Get zones using the useZones hook with searchQuery (not searchTerm)
	const {
		data: zones,
		isLoading,
		error,
		refetch,
	} = useZones({
		config: useAccountConfig({
			params: {
				page: pagination.current,
				per_page: pagination.pageSize,
				query: searchQuery,
				include:
					'box_allocations,state,groups.schedules,store.lastGooglePlaceData,last_played_song,last_played_schedule',
				...(accountFilter !== null && { account_id: accountFilter }),
			},
		}),
		options: {
			retry: false,
			enabled: activeTab === 'zones',
			refetchInterval: activeTab === 'zones' ? 30000 : false,
			refetchIntervalInBackground: false,
		},
	})

	const hasZonesData = Boolean(zones)
	const shouldShowZonesSkeleton = isLoading && !hasZonesData

	// Get locations
	const {
		data: locations,
		isLoading: isLocationsLoading,
		isFetching: isLocationsFetching,
		error: locationsError,
		refetch: refetchLocations,
	} = useLocations({
		config: useAccountConfig({
			params: {
				page: locationPagination.current,
				per_page: locationPagination.pageSize,
				query: locationSearchQuery,
				include: 'players,account',
				...(accountFilter !== null && { account_id: accountFilter }),
			},
		}),
		options: {
			retry: false,
			enabled: activeTab === 'locations',
		},
	})

	// Get groups
	const {
		data: groups,
		isLoading: isGroupsLoading,
		isFetching: isGroupsFetching,
		error: groupsError,
		refetch: refetchGroups,
	} = useGroups({
		config: useAccountConfig({
			params: {
				page: groupPagination.current,
				per_page: groupPagination.pageSize,
				query: groupSearchQuery,
				include: 'analytics,mixes,account',
				...(accountFilter !== null && { account_id: accountFilter }),
			},
		}),
		options: {
			retry: false,
			enabled: activeTab === 'group',
		},
	})

	useEffect(() => {
		if (error) {
			navigate('/dashboard')
		}
	}, [error])

	// Effect to refetch when a zone is deleted
	useEffect(() => {
		if (deleteCounter > 0) {
			refetch()
		}
	}, [deleteCounter, refetch])

	// Check for newly created zone from navigation state
	useEffect(() => {
		const state = location.state as { newZoneId?: number } | null
		if (state?.newZoneId) {
			setNewZoneId(state.newZoneId)
			setShowSuccessModal(true)
			window.history.replaceState({}, document.title)
		}
	}, [location.state])

	// Remove showGuide parameter from URL
	const removeShowGuideParam = React.useCallback(() => {
		if (params.has('showGuide')) {
			const newParams = new URLSearchParams(params)
			newParams.delete('showGuide')
			setParams(newParams, { replace: true })
		}
	}, [params, setParams])

	// Show onboarding modal when:
	// 1. ?showGuide=true is present (forced from guide), OR
	// 2. ALL zones have a pending code (none are activated) AND it's a trial account
	useEffect(() => {
		if (
			!onboardingModalShownRef.current &&
			activeTab === 'zones' &&
			zones?.data &&
			zones.data.length > 0 &&
			!isLoading
		) {
			// Check if ALL zones have a code (pending activation)
			const allZonesHaveCode = zones.data.every((zone) => !!zone.code)
			// Check if any zone is online (activated)
			const hasActiveZone = zones.data.some((zone) => zone.state?.isOnline())

			// Show onboarding when:
			// 1. showGuide=true param is present (forced from guide), OR
			// 2. All zones have code, none activated, and it's a trial account
			const shouldShowFromGuide =
				showGuideParam && allZonesHaveCode && !hasActiveZone
			const shouldShowAutomatically =
				allZonesHaveCode && !hasActiveZone && isTrialAccount

			if (shouldShowFromGuide || shouldShowAutomatically) {
				onboardingModalShownRef.current = true
				setShowOnboardingModal(true)
			}
		}
	}, [zones?.data, isLoading, activeTab, isTrialAccount, showGuideParam])

	const handleOnboardingComplete = () => {
		setSelectedPendingZone(null)
		setShowOnboardingModal(false)
		setShowOnboardingSuccessModal(true)
		removeShowGuideParam()
	}

	const handleOnboardingSkip = () => {
		setSelectedPendingZone(null)
		setShowOnboardingModal(false)
		removeShowGuideParam()
	}

	// Get the first pending (not activated) zone for onboarding
	const pendingZone =
		selectedPendingZone ||
		zones?.data?.find((zone) => !zone.state?.isOnline()) ||
		null

	// Get IDs of schedules already assigned to any zone's groups
	const assignedScheduleIds = React.useMemo(() => {
		if (!zones?.data) return []
		const allSchedules = zones.data.flatMap((zone) =>
			zone.getSchedulesAssigned()
		)
		// Get unique IDs
		return [...new Set(allSchedules.map((s) => s.id))]
	}, [zones?.data])

	// Get private schedules for onboarding step 3
	const { data: privateSchedulesData, refetch: refetchPrivateSchedules } =
		useSchedules({
			config: useAccountConfig({
				params: {
					per_page: 1,
					public: false,
					include: 'timeslots',
				},
			}),
			options: {
				enabled: showOnboardingModal,
			},
		})

	// Get recommended schedules for onboarding step 3
	const { data: recommendedSchedules } = useRecommendedSchedules({
		config: useAccountConfig({}),
		options: {
			enabled: showOnboardingModal,
		},
	})

	// Get groups with schedules for onboarding step 3
	const { data: groupsWithSchedules } = useGroups({
		config: useAccountConfig({
			params: {
				per_page: 100,
				include: 'schedules',
			},
		}),
		options: {
			enabled: showOnboardingModal,
		},
	})

	// Combine recommended schedules with group schedules (removing duplicates)
	// Sort with assigned schedules first
	const combinedSchedules = React.useMemo(() => {
		const recommended = recommendedSchedules || []
		const groupSchedules =
			groupsWithSchedules?.data?.flatMap((group) => group.schedules || []) || []

		// Combine and remove duplicates by id
		const allSchedules = [...recommended, ...groupSchedules]
		const uniqueSchedules = allSchedules.reduce(
			(acc, schedule) => {
				if (!acc.find((s) => s.id === schedule.id)) {
					acc.push(schedule)
				}
				return acc
			},
			[] as typeof recommended
		)

		// Sort: assigned schedules first, then the rest
		return uniqueSchedules.sort((a, b) => {
			const aIsAssigned = assignedScheduleIds.includes(a.id)
			const bIsAssigned = assignedScheduleIds.includes(b.id)
			if (aIsAssigned && !bIsAssigned) return -1
			if (!aIsAssigned && bIsAssigned) return 1
			return 0
		})
	}, [recommendedSchedules, groupsWithSchedules, assignedScheduleIds])

	const handleConnectThisDevice = () => {
		// TODO: Implement connect on this device flow
		// This could open the app download or activation flow
		console.log('Connect on this device')
	}

	const handleConnectZone = (zone: Zone) => {
		setSelectedPendingZone(zone)
		setShowConnectSheet(true)
	}

	const handleZoneActivated = () => {
		setSelectedPendingZone(null)
		// Zone was activated via code on another device
		// Don't close modal - step 3 will show
		// Refetch to update the list
		refetch()
	}

	const handleConnectThisDeviceSheet = async () => {
		setShowConnectSheet(false)
		handleConnectThisDevice()
		if (selectedPendingZone?.code) {
			try {
				await navigator.clipboard.writeText(selectedPendingZone.code)
			} catch (error) {
				console.error('Failed to copy zone code:', error)
			}
		}
		showNotification({
			type: 'success',
			message: selectedPendingZone?.code
				? t('modals.zoneOnboarding.notifications.codeCopied')
				: t('modals.zoneOnboarding.notifications.zoneConnected'),
		})
	}

	const handleConnectOtherDeviceSheet = async () => {
		setShowConnectSheet(false)
		// TODO: Implement connect on other device flow
		console.log('Connect on other device')
		if (selectedPendingZone?.code) {
			try {
				await navigator.clipboard.writeText(selectedPendingZone.code)
			} catch (error) {
				console.error('Failed to copy zone code:', error)
			}
		}
		showNotification({
			type: 'success',
			message: selectedPendingZone?.code
				? t('modals.zoneOnboarding.notifications.codeCopied')
				: t('modals.zoneOnboarding.notifications.zoneConnected'),
		})
	}

	const handleScheduleMusic = (scheduleIds: number[]) => {
		// Get the first group from any zone
		const firstGroup = zones?.data
			?.flatMap((zone) => zone.groups || [])
			?.find((group) => group?.id)

		if (!firstGroup) {
			console.warn('No group found to assign schedules')
			return
		}

		const groupId = firstGroup.id

		// Calculate which schedules to add and remove
		const schedulesToAdd = scheduleIds.filter(
			(id) => !assignedScheduleIds.includes(id)
		)
		const schedulesToRemove = assignedScheduleIds.filter(
			(id) => !scheduleIds.includes(id)
		)

		// Add new schedules
		if (schedulesToAdd.length > 0) {
			addSchedulesToGroup({
				groupId,
				data: { schedules: schedulesToAdd },
			})
		}

		// Remove deselected schedules
		if (schedulesToRemove.length > 0) {
			removeSchedulesFromGroup({
				groupId,
				data: { schedules: schedulesToRemove },
			})
		}

		// Don't close modal - let it advance to step 4 (activation)
		refetch()
	}

	const handleExportZones = () => {
		exportZones({})
	}

	const handleAddZone = () => {
		navigate('/zones/create')
	}

	const handleUpdateZone = (zoneId: number) => (data: UpdateZoneRequestDTO) => {
		updateZone({ zoneId, data })
	}

	const handleCloseSuccessModal = () => {
		setShowSuccessModal(false)
		// NOTE: do not clear newZoneId here; the user may choose a follow-up action
		// (assign/add content) which needs the zone id.
	}

	const handleAssignToGroup = () => {
		if (!newZoneId) return
		setShowSuccessModal(false)
		setShowFinishAssignModal(true)
	}

	const handleAddContentToZone = () => {
		if (!newZoneId) return
		setShowSuccessModal(false)
		setShowFinishAddContentModal(true)
	}

	const handleCloseFinishFlowModals = () => {
		if (zoneFinishFlow.isSubmitting) return
		setShowFinishAssignModal(false)
		setShowFinishAddContentModal(false)
		setNewZoneId(null)
	}

	const zoneFinishFlow = useZoneFinishFlow({
		newZoneId,
		refetchZones: refetch,
		onFinishAssignSuccess: () => {
			setShowFinishAssignModal(false)
			setNewZoneId(null)
		},
		onFinishAddContentSuccess: () => {
			setShowFinishAddContentModal(false)
			setNewZoneId(null)
		},
	})

	return (
		<>
			<ZonesUI
				activeTab={activeTab}
				handleTabChange={handleTabChange}
				zonesTab={{
					zones,
					isLoading: shouldShowZonesSkeleton,
					error,
					searchTerm,
					statusFilter,
					setSearchTerm: handleSearch,
					setStatusFilter,
					handleExportZones,
					openDeleteModal: zoneDeletion.openDeleteModal,
					openRefreshModal: zoneRefresh.openRefreshModal,
					openEditModal: zoneEdit.openEditModal,
					openSendCodeEmailModal: zoneSendCodeEmail.openSendCodeEmailModal,
					handleAddZone,
					handleConnectZone,
					handleUpdateZone,
					isUpdating,
					isTrialAccount: !!isTrialAccount,
					pagination,
					accountFilter,
					setAccountFilter,
					showAccountFilter: fetchAllDataEnabled,
				}}
				locationsTab={{
					locations,
					isLoading: isLocationsLoading || isLocationsFetching,
					error: locationsError,
					searchTerm: locationSearchTerm,
					setSearchTerm: handleLocationSearch,
					pagination: locationPagination,
					refetch: refetchLocations,
					accountFilter,
					setAccountFilter,
					showAccountFilter: fetchAllDataEnabled,
				}}
				groupsTab={{
					groups,
					isLoading: isGroupsLoading || isGroupsFetching,
					error: groupsError,
					searchTerm: groupSearchTerm,
					setSearchTerm: handleGroupSearch,
					pagination: groupPagination,
					refetch: refetchGroups,
					accountFilter,
					setAccountFilter,
					showAccountFilter: fetchAllDataEnabled,
				}}
			/>

			<DeleteZoneModal
				visible={zoneDeletion.isModalVisible}
				zone={zoneDeletion.selectedZone}
				onClose={zoneDeletion.closeDeleteModal}
				onConfirm={zoneDeletion.handleDeleteZone}
				isDeleting={zoneDeletion.isDeleting}
				onPage="zones"
			/>

			<RefreshZoneModal
				visible={zoneRefresh.isModalVisible}
				onClose={zoneRefresh.closeRefreshModal}
				onConfirm={zoneRefresh.handleRefreshZone}
				isRefreshing={zoneRefresh.isRefreshing}
			/>

			<SendCodeEmailZoneModal
				visible={zoneSendCodeEmail.isModalVisible}
				zone={zoneSendCodeEmail.selectedZone}
				onClose={zoneSendCodeEmail.closeSendCodeEmailModal}
				onConfirm={zoneSendCodeEmail.handleSendCodeEmail}
				isSending={zoneSendCodeEmail.isSending}
			/>

			<ZoneCreatedSuccessModal
				visible={showSuccessModal}
				zoneId={newZoneId}
				onClose={handleCloseSuccessModal}
				onAssignToGroup={handleAssignToGroup}
				onAddContent={handleAddContentToZone}
			/>

			<AddGroupToZone
				visible={showFinishAssignModal}
				zone={createdZone || null}
				onClose={handleCloseFinishFlowModals}
				onConfirm={(selectedGroupIds) =>
					zoneFinishFlow.confirmFinishAssign(selectedGroupIds)
				}
				isAssigning={zoneFinishFlow.isSubmitting}
			/>

			<AddContentToZoneModal
				visible={showFinishAddContentModal}
				onClose={handleCloseFinishFlowModals}
				initialTab="playlists"
				onConfirm={zoneFinishFlow.confirmFinishAddContent}
				isSubmitting={zoneFinishFlow.isSubmitting}
			/>

			<EditZoneModal
				visible={zoneEdit.isModalVisible}
				zone={zoneEdit.selectedZone}
				onClose={zoneEdit.closeEditModal}
				onSubmit={zoneEdit.handleEditZone}
				isLoading={zoneEdit.isEditing}
			/>

			<ZoneOnboardingModal
				visible={showOnboardingModal}
				pendingZone={pendingZone}
				recommendedSchedules={combinedSchedules}
				privateSchedulesData={privateSchedulesData}
				assignedScheduleIds={assignedScheduleIds}
				onComplete={handleOnboardingComplete}
				onConnectThisDevice={handleConnectThisDevice}
				onZoneActivated={handleZoneActivated}
				onScheduleMusic={handleScheduleMusic}
				refetchZones={refetch}
				refetchPrivateSchedules={refetchPrivateSchedules}
				canSkip={!isTrialAccount || isSwitchedAccount}
				onSkip={handleOnboardingSkip}
			/>

			<ZoneOnboardingSuccessModal
				visible={showOnboardingSuccessModal}
				onClose={() => setShowOnboardingSuccessModal(false)}
			/>

			<BottomSheet open={showConnectSheet} onOpenChange={setShowConnectSheet}>
				<BottomSheetContent className="px-4 pb-6">
					<BottomSheetTitle className="sr-only">
						{t('modals.zoneOnboarding.step4.title')}
					</BottomSheetTitle>
					<BottomSheetDescription className="sr-only">
						{t('modals.zoneOnboarding.step4.description')}
					</BottomSheetDescription>
					<div className="space-y-3 pt-8 flex flex-col justify-center">
						<Button
							onClick={handleConnectThisDeviceSheet}
							className="w-full h-12 text-base font-medium rounded-full bg-white text-black hover:bg-gray-200"
						>
							{t('modals.zoneOnboarding.step4.connectThisDevice')}
						</Button>
						<Button
							onClick={handleConnectOtherDeviceSheet}
							variant="outline"
							className="w-full h-12 text-base font-medium rounded-full border-gray-600 text-white hover:bg-gray-800"
						>
							{t('modals.zoneOnboarding.step4.connectOtherDevice')}
						</Button>
					</div>
				</BottomSheetContent>
			</BottomSheet>
		</>
	)
}

export default Zones
